import { format } from 'date-fns';
import { PaperSize, FrameOrientation } from 'src/types';

import { downloadImageFromDataUrl } from './export-image-core';
import { FrameObject, exportPdfFrame } from './export-image-pdf';
import {
  exportImagePosition,
  ImagePositionOptions,
} from './export-image-position';

export interface IExportFrameProps {
  element: HTMLElement;
  frameObject: FrameObject;
  pixelRatio: number;
  projectName: string;
  /** Optional base name for exported file (without extension). e.g. projectName_revision_exportDate */
  fileName?: string;
}

/**
 * Downloads a frame as PNG image
 */
export async function downloadImageFrame({
  element,
  frameObject,
  pixelRatio = 4,
  projectName,
  fileName,
}: IExportFrameProps): Promise<void> {
  const dataUrl = await exportImagePosition({
    element,
    imageOptions: frameObject as ImagePositionOptions,
    pixelRatio,
  });
  const baseName = fileName ?? projectName;
  downloadImageFromDataUrl(dataUrl, `${baseName}.png`);
}

/**
 * Main export function that handles both PNG and PDF exports
 */
export async function exportFrame(
  type: 'png' | 'pdf',
  {
    element,
    frameObject,
    pixelRatio = 4,
    projectName,
    paperSize,
    orientation,
    fileName,
  }: IExportFrameProps & {
    paperSize: PaperSize;
    orientation: FrameOrientation;
  },
): Promise<void> {
  if (type === 'pdf') {
    return exportPdfFrame({
      element,
      frameObject,
      pixelRatio,
      projectName,
      paperSize,
      orientation,
      fileName,
    });
  }
  return downloadImageFrame({
    element,
    frameObject,
    pixelRatio,
    projectName,
    fileName,
  });
}

export function getExportFileName(
  projectName: string,
  revision: string,
): string {
  const exportDate = format(new Date(), 'yyyy-MM-dd');
  const safe = (s: string) =>
    s.replace(/[/\\:*?"<>|\s]+/g, '_').trim() || 'export';
  return [projectName, revision, exportDate]
    .filter(Boolean)
    .map(safe)
    .join('_');
}
